/**
 * Timezone utility functions for consistent date/time display across the app
 * Uses date-fns for reliable date handling
 *
 * TIMEZONE HANDLING PATTERN:
 * - Backend stores all timestamps in UTC (naive datetimes)
 * - Frontend sends dates as YYYY-MM-DD + timezone name for proper conversion
 * - Backend converts using user's timezone to get correct UTC range
 * - Display dates are shown in user's local timezone
 *
 * Example: User in PST selects "Jan 26"
 * - Frontend sends: { date: "2026-01-26", timezone: "America/Los_Angeles" }
 * - Backend converts: Jan 26 00:00 PST = Jan 26 08:00 UTC
 *                     Jan 26 23:59 PST = Jan 27 07:59 UTC
 * - User sees data from their full day, not UTC midnight to midnight
 */
import { format, parseISO } from 'date-fns'

/**
 * Get the current timezone abbreviation (e.g., "PST", "EST", "UTC")
 * Falls back to the full timezone name if abbreviation is not available
 */
export const getTimezoneAbbr = (): string => {
  const date = new Date()
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  // Try to get short timezone name
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
    timeZone
  })
  const parts = formatter.formatToParts(date)
  const tzPart = parts.find(p => p.type === 'timeZoneName')

  return tzPart?.value || timeZone
}

/**
 * Get the full timezone name (e.g., "America/Los_Angeles")
 */
export const getTimezoneName = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Parse an ISO date string from the backend.
 * Backend dates are stored in UTC but may be returned without 'Z' suffix.
 * This function ensures consistent UTC parsing.
 *
 * @param dateStr - ISO date string from backend (e.g., "2026-01-05T12:46:00")
 * @returns Date object representing the UTC time
 */
export const parseBackendDate = (dateStr: string | null): Date | null => {
  if (!dateStr) return null

  // If the string doesn't have a 'Z' or timezone offset, append 'Z' to treat as UTC
  let normalizedStr = dateStr
  if (!dateStr.endsWith('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
    normalizedStr = `${dateStr}Z`
  }

  return parseISO(normalizedStr)
}

/**
 * Format a date string with timezone abbreviation
 * Assumes the input is UTC from the backend
 * @param dateStr - ISO date string or Date object
 * @returns Formatted date string with timezone (e.g., "1/6/2026, 8:46:00 PM PST")
 */
export const formatDateWithTimezone = (dateStr: string | Date | null): string => {
  if (!dateStr) return '-'
  const tzAbbr = getTimezoneAbbr()

  let date: Date | null
  if (typeof dateStr === 'string') {
    date = parseBackendDate(dateStr)
  } else {
    date = dateStr
  }

  if (!date || isNaN(date.getTime())) return '-'

  return `${format(date, 'M/d/yyyy, h:mm:ss a')} ${tzAbbr}`
}

/**
 * Format a date string without timezone (just local time)
 * Assumes the input is UTC from the backend
 * @param dateStr - ISO date string or Date object
 * @returns Formatted date string (e.g., "1/6/2026, 8:46:00 PM")
 */
export const formatDate = (dateStr: string | Date | null): string => {
  if (!dateStr) return '-'

  let date: Date | null
  if (typeof dateStr === 'string') {
    date = parseBackendDate(dateStr)
  } else {
    date = dateStr
  }

  if (!date || isNaN(date.getTime())) return '-'

  return format(date, 'M/d/yyyy, h:mm:ss a')
}

/**
 * Format a UTC date string for use in datetime-local input (LOCAL time)
 * The datetime-local input expects format: YYYY-MM-DDTHH:mm
 *
 * @param dateStr - ISO date string from backend (UTC)
 * @returns String formatted for datetime-local input (YYYY-MM-DDTHH:mm) in local time
 */
export const formatForDatetimeLocalInput = (dateStr: string | null): string => {
  if (!dateStr) return ''

  const date = parseBackendDate(dateStr)
  if (!date || isNaN(date.getTime())) return ''

  // Format in local time for datetime-local input
  return format(date, "yyyy-MM-dd'T'HH:mm")
}

/**
 * Convert a datetime-local input value (local time) to UTC ISO string for the backend
 *
 * @param localDateStr - Value from datetime-local input (e.g., "2026-01-05T04:46")
 * @returns ISO string in UTC for the backend
 */
export const datetimeLocalToUtc = (localDateStr: string): string => {
  if (!localDateStr) return ''

  // datetime-local value is in local time, create a Date which interprets it as local
  const date = new Date(localDateStr)
  if (isNaN(date.getTime())) return ''

  // toISOString() converts to UTC
  return date.toISOString()
}

// ============================================================================
// DATE DISPLAY UTILITIES
// ============================================================================

/**
 * Format a date string for date-only display (no time component).
 *
 * Two modes based on input format:
 * - YYYY-MM-DD (date picker): treated as a local date. "2026-02-06" = Feb 6 in user's timezone.
 * - ISO datetime (backend): treated as a UTC timestamp, converted to user's local timezone.
 *   "2026-02-06T00:00:00Z" in PST = Feb 5 at 4pm local, so displays "Feb 5".
 *
 * @param dateStr - Date string (YYYY-MM-DD or ISO datetime)
 * @param options - Intl.DateTimeFormat options (defaults to { month: 'short', day: 'numeric', year: 'numeric' })
 * @returns Formatted date string (e.g., "Feb 6, 2026")
 */
export const formatDisplayDate = (
  dateStr: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!dateStr) return ''

  const defaultOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }
  const formatOptions = options || defaultOptions

  // YYYY-MM-DD format (10 chars, no 'T') = local date from date picker
  const isDateOnly = dateStr.length === 10 && !dateStr.includes('T')

  if (isDateOnly) {
    const date = parseApiDate(dateStr)
    if (!date) return ''
    return date.toLocaleDateString('en-US', formatOptions)
  }

  // ISO datetime string from backend - convert to local timezone
  const date = parseBackendDate(dateStr)
  if (!date || isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', formatOptions)
}

// ============================================================================
// DATE FORMAT UTILITIES FOR API CALLS
// ============================================================================

/**
 * Format a Date object as YYYY-MM-DD string for API calls.
 *
 * This is the preferred format for sending dates to the backend along with
 * the user's timezone. The backend will convert to proper UTC range.
 *
 * @param date - Date object (interpreted as local time)
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Get today's date as YYYY-MM-DD string in user's local timezone.
 */
export const getTodayForApi = (): string => {
  return formatDateForApi(new Date())
}

/**
 * Get a date N days ago as YYYY-MM-DD string in user's local timezone.
 */
export const getDaysAgoForApi = (days: number): string => {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return formatDateForApi(date)
}

/**
 * Map license billing period bounds (ISO from backend) to YYYY-MM-DD for usage APIs.
 * Uses each instant interpreted in the user's local calendar (same as {@link getTodayForApi}).
 */
export const getBillingPeriodRangeForApi = (
  periodStart?: string | null,
  periodEnd?: string | null
): { start: string; end: string } | null => {
  if (!periodStart || !periodEnd) return null
  const startDt = parseBackendDate(periodStart)
  const endDt = parseBackendDate(periodEnd)
  if (!startDt || !endDt || isNaN(startDt.getTime()) || isNaN(endDt.getTime())) {
    return null
  }
  return {
    start: formatDateForApi(startDt),
    end: formatDateForApi(endDt)
  }
}

/**
 * Parse a YYYY-MM-DD string to a Date object (as local midnight).
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Date object set to local midnight, or null if invalid
 */
export const parseApiDate = (dateStr: string): Date | null => {
  if (!dateStr || dateStr.length !== 10) return null

  const [year, month, day] = dateStr.split('-').map(Number)
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null

  // Create date in local timezone
  return new Date(year, month - 1, day)
}
